import React, { useState } from "react";
import { toast } from "sonner";
import { errMsg, fmt, qs, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { Empty, ErrorBox, Loading, StatusBadge } from "../../components/kit";
import { StarRow } from "../../components/RatingPanel";

function useAct(reload: () => void) {
  const api = useApi();
  return async (path: string, body: any, ok: string) => {
    const reason = window.prompt("Reason (required, 3+ characters, recorded in the audit log)");
    if (!reason) return;
    try { await api("post", path, { ...body, reason }); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
}

export function OpsApplications() {
  const { categories, catName, areaName } = useTaxonomy();
  const [status, setStatus] = useState("pending");
  const [cat, setCat] = useState("");
  const { data, loading, error, reload } = useLoad<any[]>(`/ops/applications?${qs({ status, category_id: cat })}`);
  const act = useAct(reload);
  return (
    <section className="space-y-4" data-testid="ops-applications">
      <div className="flex flex-wrap gap-2">
        <select className="input w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" data-testid="ops-app-status-filter">
          {["", "pending", "in_review", "approved", "rejected"].map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select className="input w-auto" value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Trade" data-testid="ops-app-category-filter">
          <option value="">All trades</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <p className="text-xs text-[var(--muted)]">Approving creates a <strong>pending</strong> organization and profile only. It adds no membership, role or invitation, so access still needs the normal org approval and onboarding steps.</p>
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && !data.length && <Empty title="No applications match" />}
      <ul className="space-y-2">
        {data?.map((a) => (
          <li key={a.id} className="card text-sm" data-testid={`ops-app-${a.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2"><strong>{a.business_name}</strong><StatusBadge value={a.status} testId={`ops-app-status-${a.id}`} /></div>
            <p className="text-[var(--muted)]">{a.applicant_email || "no verified email"} · submitted {fmt(a.created_at)}</p>
            <p>Trades: {a.category_ids.map(catName).join(", ")} · Areas: {a.area_ids.map(areaName).join(", ")}</p>
            <p>{a.experience_years} yrs experience · {a.availability}{a.experience_summary && ` · ${a.experience_summary}`}</p>
            {a.decision_reason && <p className="text-xs">Last reason: {a.decision_reason}{a.org_id && ` · pending org ${a.org_id.slice(0, 8)}`}</p>}
            {["pending", "in_review"].includes(a.status) && (
              <div className="mt-2 flex flex-wrap gap-2">
                {a.status === "pending" && <button className="btn btn-ghost btn-sm" onClick={() => act(`/ops/applications/${a.id}/decision`, { action: "in_review" }, "Marked in review")} data-testid={`ops-app-review-${a.id}`}>Mark in review</button>}
                <button className="btn btn-primary btn-sm" onClick={() => act(`/ops/applications/${a.id}/decision`, { action: "approve" }, "Approved: pending org created")} data-testid={`ops-app-approve-${a.id}`}>Approve</button>
                <button className="btn btn-danger btn-sm" onClick={() => act(`/ops/applications/${a.id}/decision`, { action: "reject" }, "Rejected")} data-testid={`ops-app-reject-${a.id}`}>Reject</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function OpsReviews() {
  const [status, setStatus] = useState("");
  const [reported, setReported] = useState("");
  const { data, loading, error, reload } = useLoad<any[]>(`/ops/reviews?${qs({ status, reported })}`);
  const act = useAct(reload);
  return (
    <section className="space-y-4" data-testid="ops-reviews">
      <div className="flex flex-wrap gap-2">
        <select className="input w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" data-testid="ops-review-status-filter">
          <option value="">All</option><option value="visible">Visible</option><option value="hidden">Hidden</option>
        </select>
        <select className="input w-auto" value={reported} onChange={(e) => setReported(e.target.value)} aria-label="Reported" data-testid="ops-review-reported-filter">
          <option value="">Reported or not</option><option value="true">Reported</option><option value="false">Not reported</option>
        </select>
      </div>
      {loading && !data && <Loading />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && !data.length && <Empty title="No ratings match" />}
      <ul className="space-y-2">
        {data?.map((r) => (
          <li key={r.id} className="card text-sm" data-testid={`ops-review-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{r.author_side === "customer" ? `Customer → ${r.org_name}` : `${r.org_name} → customer`}</span>
              <div className="flex items-center gap-2"><StarRow value={r.stars} idPrefix={`ops-${r.id}`} /><StatusBadge value={r.status} testId={`ops-review-status-${r.id}`} /></div>
            </div>
            {r.reports?.map((x: any, i: number) => <p key={i} className="text-xs text-[var(--red)]">Report ({x.by_side}): {x.reason}</p>)}
            {r.moderation_reason && <p className="text-xs">Moderation: {r.moderation_reason}</p>}
            <button className={`btn btn-sm mt-2 ${r.status === "visible" ? "btn-danger" : "btn-ghost"}`} onClick={() => act(`/ops/reviews/${r.id}/moderate`, { action: r.status === "visible" ? "hide" : "restore" }, "Updated")} data-testid={`ops-review-toggle-${r.id}`}>
              {r.status === "visible" ? "Hide" : "Restore"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
